// Lib
import { AxiosPromise, AxiosResponse } from "axios";
import { oc } from "ts-optchain";

// Services
import { DealernetApi } from "./DealernetApi";
import { ObjectUtil } from "./ObjectUtil";
import { FeatureFlagService } from "@/services/FeatureFlagService"
import { SettingsService } from "@/services"

// Types
import {
  IAccountContext,
  IOemDto,
  IOrderOptionsDto,
  IOrderResponseDto,
  IPagedList,
  IShoppingCartListDtoOverride,
  IShoppingCartListItemDtoOverride,
  IShoppingCartListResponseDto,
  IShoppingCartSummaryDto,
  Nullable,
  IUserInfo,
  IFreightChargeLogDto,
} from "@/types";

import { constants } from "@/const"
import { ILiftLinkForm } from "@/components/Cart/Types";

const CONTROLLER_PATH = "ShoppingCart";

export class ShoppingCartService {
  public static async LoadShoppingCart(params: ILoadShoppingCartRequest): Promise<IShoppingCartListDtoOverride> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${CONTROLLER_PATH}/LoadShoppingCart`,
      params,
    });

    return oc(response as AxiosResponse<IShoppingCartListDtoOverride>).data()!;
  }

  public static async GetShoppingCartSummary(params: IGetShoppingCartSummaryRequest): Promise<IShoppingCartSummaryDto> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${CONTROLLER_PATH}/LoadShoppingCartSummary`,
      params,
    });

    return oc(response as AxiosResponse<IShoppingCartSummaryDto>).data()!;
  }

  public static async GetShoppingCart(params: IShoppingCartListRequest): Promise<any> {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${CONTROLLER_PATH}/ShoppingCart`,
      params,
    });
    return oc(response as AxiosResponse<any[]>).data([]);
  }

  public static async EditShoppingCart(params: IShoppingCartListRequest, data: any): Promise<any> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/EditShoppingCart`,
      data: data,
      params,
    });

    return oc(response as AxiosResponse<any>).data()!;
  }

  public static AddShoppingCartItem(
    params: IAddShoppingCartItemRequest
  ): AxiosPromise<IShoppingCartListItemDtoOverride> {
    return DealernetApi.client().request({
      method: "get",
      url: `${CONTROLLER_PATH}/AddShoppingCartItem`,
      params,
    });
  }

  public static async AddShoppingCartItems(
    request: IAddShoppingCartItemsRequest
  ): Promise<IShoppingCartListItemDtoOverride[]> {
    const { partOemPartCodesAndQuantities, ...params } = request;

    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/AddShoppingCartItems`,
      data: partOemPartCodesAndQuantities,
      params,
    });

    return oc(response as AxiosResponse<IShoppingCartListItemDtoOverride[]>).data()!;
  }

  public static async GetValidatedShoppingCartItems(
    data: IGetValidateShoppingCartItemsInput
  ): Promise<IShoppingCartListResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/GetValidatedShoppingCartItems`,
      data,
    });
    return oc(response as AxiosResponse<IShoppingCartListResponseDto>).data()!;
  }

  public static async EditShoppingCartItem(data: IEditShoppingCartItemInput): Promise<IShoppingCartListResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/EditShoppingCartItem`,
      data,
    });

    return oc(response as AxiosResponse<IShoppingCartListResponseDto>).data()!;
  }

  public static async GetAllShoppingCartDetails(data: IGetAllShoppingCartDetailsRequest): Promise<IShoppingCartListResponseDto> {

    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/GetAllShoppingCartDetails`,
      data
    });

    return oc(response as AxiosResponse<IShoppingCartListResponseDto>).data()!;
  }

  public static async PlaceOrder(data: IPlaceOrderRequest): Promise<IOrderResponseDto[]> {

    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/PlaceOrder`,
      data
    });

    return oc(response as AxiosResponse<IOrderResponseDto[]>).data()!;
  }

  public static async GetOems(data: IGetOemsRequest): Promise<IPagedList<IOemDto>> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/GetOems`,
      data
    });

    const result = oc(response as AxiosResponse<IPagedList<IOemDto>>).data();

    return (
      result ||
      ObjectUtil.emptyPagedList<IOemDto>({
        PageSize: data.pageSize,
      })
    );
  }

  public static async RemoveCartItems(input: IRemoveShoppingCartItemsInput): Promise<IShoppingCartListResponseDto> {
    const { shoppingCartId, itemIds, ...getInput } = input;

    itemIds.forEach(async (itemId) => {
      const params = {
        accountId: input.accountId,
        userId: input.userId,
        shoppingCartId,
        itemId,
      };

      const response = await DealernetApi.client().request({
        method: "delete",
        url: `${CONTROLLER_PATH}/DeleteShoppingCartItem`,
        params,
      });

      if (oc(response as AxiosResponse<boolean>).data() !== true) {
        return Promise.reject(`Failed to remove ${itemId} from cart`);
      }
    });

    return ShoppingCartService.GetValidatedShoppingCartItems(getInput);
  }

  public static async EmptyCart(params: IEmptyCartRequest): Promise<boolean> {
    const response = await DealernetApi.client().request({
      method: "delete",
      url: `${CONTROLLER_PATH}/EmptyCart`,
      params,
    });

    return oc(response as AxiosResponse<boolean>).data(true);
  }

  public static getOrderTypePref(): string {
    return localStorage.getItem("shopping-cart-order-type") || "Emergency";
  }

  public static setOrderTypePref(orderType: "Emergency" | "Stock") {
    localStorage.setItem("shopping-cart-order-type", orderType);
  }

  public static getAppliedPromoCodes(): string[] {
    let promos = localStorage.getItem("shopping-cart-promo-codes");
    if (!promos) {
      return [];
    } else {
      return JSON.parse(promos);
    }
  }

  public static setPromoCodes(promoCodes: string[]) {
    localStorage.setItem("shopping-cart-promo-codes", JSON.stringify(promoCodes));
  }

  public static async getFreightCharge(partsPrice: number, shippingCode: string) {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${CONTROLLER_PATH}/GetFreightCharge?partsPrice=${partsPrice}&shippingCode=${shippingCode}`
    });

    return response;
  }

  public static async getExxonDistributor(params: IGetShoppingCartSummaryRequest) {
    const response = await DealernetApi.client().request({
      method: "get",
      url: `${CONTROLLER_PATH}/GetExxonDistributor`,
      params
    });

    return response;
  }

  public static async logFreightCharge(data: IFreightChargeLogDto) {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/LogFreightCharge`,
      data: data
    });

    return response;
  }
  public static async isOrderingDisabled() {
    var response = await SettingsService.getAllSettings();
    var settings = response;
    var settingKeys = constants.MLA_ORDERS_BLACK_SETTINGS_KEYS;
    var startDateSetting = _.find(settings, (x) => x.Key == settingKeys.START_DATE).Value;
    var endDateSetting = _.find(settings, (x) => x.Key == settingKeys.END_DATE).Value;

    var ordersDisabledStartTime = new Date(startDateSetting);
    var ordersDisabledEndTime = new Date(endDateSetting);
    // Force Eastern Time otherwise this will be set based on the users timezone
    var today = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York', }));

    var isInDisabledPeriod = (today.getTime() < ordersDisabledEndTime.getTime() && today.getTime() > ordersDisabledStartTime.getTime())
    var test = isInDisabledPeriod && FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.ORDERS_DISABLED);
    return test;
  }
}

export interface ILoadShoppingCartRequest extends IAccountContext {
  channelAuthorizationCode?: string;
  promoCodes?: string;
  overrideCartId?: number;
}

// tslint:disable-next-line: no-empty-interface
export interface IGetShoppingCartSummaryRequest extends IAccountContext { }

export interface IAddShoppingCartItemRequest extends IAccountContext {
  shoppingCartId: number;
  partId: number;
  quantity: number;
}

export interface IAddShoppingCartItemsRequest extends IAccountContext {
  shoppingCartId: number;
  partOemPartCodesAndQuantities: Record<string, number>;
}

export interface IGetAllShoppingCartDetailsRequest extends IAccountContext {
  userAudiences: number[];
  currencyDecimalDigits?: number;
  isPartsFastPartsFree?: boolean;
  overrideCartId?: number;
  editedPartId?: number;
  orderOptions: IOrderOptionsDto;
  isPartsStoreOrder?: boolean;
}

export interface IPlaceOrderRequest {
  accountId: number;
  user: IUserInfo;
  shoppingCartId: number;
  formattedTotalPrice: string;
  orderOptions: IOrderOptionsDto;
  liftLinkForm: ILiftLinkForm;
}

export interface IEmptyCartRequest extends IAccountContext {
  mcfCostCenterId: number;
  shoppingCartId: number;
}

export interface IEditShoppingCartItemInput extends IGetValidateShoppingCartItemsInput {
  partListId?: number;
  itemId: number;
  item: {
    itemId: number;
    dealerPartNumber: string;
    oemId: number;
    oemPartCode: string;
    quantity: number;
    stockingLocation: string;
  };
}

export interface IRemoveShoppingCartItemsInput extends IGetValidateShoppingCartItemsInput {
  shoppingCartId?: number;
  itemIds: number[];
}

export interface IGetOemsRequest extends IAccountContext {
  oemId?: number;
  oemCode?: string;
  description?: string;
  isOkToReverseCross?: boolean;
  userAudiences?: number[];
  page?: number;
  pageSize: number;
}

export interface IGetValidateShoppingCartItemsInput {
  accountId: number;
  userId: number;
  userAudiences: number[];
  promoCodes?: Nullable<string[]>;
  orderType?: Nullable<string>;
  currencyDecimalDigits?: number;
}


export interface IShoppingCartListRequest extends IAccountContext {
  accountId: number,
  userId: number,
  partListId: number,
  promoCodes: string,
}